'use strict';

var _ = require('lodash');
var CriteriaRuleService = function CriteriaRuleService($log, configurationService, momentService, featureToggleService, ruleVisibilityService) {
  var searchContexts = require('../config/rules.json');
  function filterByFeature(category) {
    category.criteria = _.filter(category.criteria, function (value) {
      return !(value.showIfFeature && featureToggleService.isDisabled(value.showIfFeature) || value.showIfNotFeature && featureToggleService.isEnabled(value.showIfNotFeature) || value.hideIfFeature && featureToggleService.isEnabled(value.hideIfFeature));
    });
  }
  function filterByVisibility(category) {
    category.criteria = _.filter(category.criteria, function (value) {
      return ruleVisibilityService.isRuleVisible(value.visibilityCondition);
    });
  }
  function mapByCategoryName(level, category, context) {
    var mappedCriteriaNameForData = {};
    var mappedMetaCategory = {};
    var mappedShortCriteriaName = {};
    var mappedCategory = {};
    var mappedFields = {};
    var mappedSelectionLimits = {};
    var mappedLimits = {};
    category.criteria.forEach(function (criterion) {
      var mappedOperators = {};
      criterion.operators.forEach(function (operator) {
        mappedOperators[operator.operator] = operator.valueType;
      });
      mappedCriteriaNameForData[criterion.criteriaName] = criterion.criteriaNameForData;
      mappedMetaCategory[criterion.criteriaName] = criterion.metaCategory;
      mappedShortCriteriaName[criterion.criteriaName] = criterion.shortCriterionName;
      mappedCategory[criterion.criteriaName] = mappedOperators;
      mappedFields[criterion.criteriaName] = criterion.field;
      mappedSelectionLimits[criterion.criteriaName] = criterion.selectionLimit;
      mappedLimits[criterion.criteriaName] = criterion.limits;
    });
    context.criteriaNameForData[category.categoryName] = mappedCriteriaNameForData;
    context.metaCategories[level] = context.metaCategories[level] || {};
    context.metaCategories[level][category.categoryName] = mappedMetaCategory;
    context.shortCriteriaNames[level] = context.shortCriteriaNames[level] || {};
    context.shortCriteriaNames[level][category.categoryName] = mappedShortCriteriaName;
    context.categories[level] = context.categories[level] || {};
    context.categories[level][category.categoryName] = mappedCategory;
    context.fields[level] = context.fields[level] || {};
    context.fields[level][category.categoryName] = mappedFields;
    context.categoriesSingleStates[category.categoryName] = category.isSingle || false;
    context.selectionLimits[category.categoryName] = mappedSelectionLimits;
    context.hiddenState[category.categoryName] = category.isHidden || _.isEmpty(category.criteria) || false;
    context.showIfFeatureState[category.categoryName] = category.showIfFeature;
    context.limits[level] = context.limits[level] || {};
    context.limits[level][category.categoryName] = mappedLimits;
  }
  var getSearchContext = _.memoize(function (searchContext) {
    var context = {
      criteriaNameForData: {},
      metaCategories: {},
      categories: {},
      fields: {},
      shortCriteriaNames: {},
      categoriesSingleStates: {},
      valid: true,
      selectionLimits: {},
      hiddenState: {},
      showIfFeatureState: {},
      limits: {},
      levels: []
    };
    searchContext = removeOptGroupSuffix(searchContext);
    if (!Array.isArray(searchContexts[searchContext])) {
      throw new Error('Invalid Search Context');
    }
    _.cloneDeep(searchContexts[searchContext]).forEach(function (rule) {
      if (!(rule.showIfFeature && featureToggleService.isDisabled(rule.showIfFeature))) {
        context.levels.push(rule.level);
        rule.categories.forEach(function (category) {
          if (!category.inactive) {
            filterByFeature(category);
            filterByVisibility(category);
            mapByCategoryName(rule.level, category, context);
          }
        });
      }
    });
    return context;
  });
  function removeOptGroupSuffix(searchContext) {
    if (searchContext && searchContext.endsWith('-opt-group')) {
      return searchContext.substr(0, searchContext.indexOf('-opt-group'));
    }
    return searchContext;
  }

  /**
   * AVPRM-8972 Adding possibility for search rules to select category by use only once.
   * Returns true if category is not single select OR if single select category wasn't selected yet
   * @param {Object} rule - SearchCriteriaRule
   * @param {Array} rules
   * @param {Boolean} isSingleSelect
   * @param {String} categoryName - like "Location", "Time & Date"
   * @returns {Boolean} - will be true if category is not single select OR if single select category wasn't selected yet
   */
  function isCategoryAvailableForSelection(rule, rules, isSingleSelect, categoryName) {
    return !(isSingleSelect && rule.category !== categoryName && _.some(rules, {
      category: categoryName
    }));
  }

  /**
   * Determines if criteria is for implicit search and not supposed to be shown in Search Modal Window
   * @param {Object} context
   * @param {String} categoryName
   * @returns {Boolean}
   */
  function isCategoryNotHidden(context, categoryName) {
    return context.hiddenState[categoryName] !== true;
  }
  function getCategories(searchContext, level, rule, rules) {
    var context = getSearchContext(searchContext);
    if (level === undefined) {
      $log.debug('Level is undefined for \"' + searchContext + '\". Using \"Default\" level...');
      level = 'Default';
    }
    return _.transform(context.categoriesSingleStates, function (result, categorySingleState, categoryName) {
      if (isCategoryAvailableForSelection(rule, rules, categorySingleState, categoryName) && isCategoryNotHidden(context, categoryName) && isAtCorrectLevel(context, level, categoryName) && isCategoryAvailableForFeatures(context, categoryName)) {
        result.push(categoryName);
      }
    }, []);
  }
  function getLevels(searchContext) {
    var context = getSearchContext(searchContext);
    return context.levels;
  }
  function getCriteria(searchContext, level, category) {
    if (!level || !category || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.keys(context.categories[level][category]);
  }
  function getCriteriaNameForData(searchContext, category, criteria) {
    if (!category || !criteria || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.get(context.criteriaNameForData, [category, criteria]);
  }
  function getOperators(searchContext, level, category, criteria) {
    if (!level || !category || !criteria || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.keys(context.categories[level][category][criteria]);
  }
  function getField(searchContext, level, category, criteria) {
    if (!level || !category || !criteria || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.get(context.fields, [level, category, criteria].join('.'));
  }
  function getSelectionLimit(searchContext, category, criteria) {
    if (!category || !criteria || !searchContext) {
      return 0;
    }
    var context = getSearchContext(searchContext);
    var limitToParse = _.get(context.selectionLimits, [category, criteria].join('.'));
    if (!limitToParse) {
      return 0;
    } else if (typeof limitToParse === 'number') {
      return limitToParse;
    } else {
      return configurationService[limitToParse.slice(2, -2)]();
    }
  }
  function getMetaCategory(searchContext, level, category, criteria) {
    if (!level || !category || !criteria || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.get(context.metaCategories, [level, category, criteria].join('.'));
  }
  function getShortCriterionName(searchContext, level, category, criteria) {
    if (!level || !category || !criteria || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.get(context.shortCriteriaNames, [level, category, criteria]);
  }
  function getValueType(searchContext, level, category, criteria, operator) {
    if (!level || !category || !criteria || !operator || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.get(context.categories, [level, category, criteria, operator].join('.'));
  }
  function getOperandLimits(searchContext, level, category, criteria) {
    if (!level || !category || !criteria || !searchContext) {
      return null;
    }
    var context = getSearchContext(searchContext);
    return _.get(context.limits, [level, category, criteria].join('.'));
  }
  function getDefaultFieldValue(key) {
    var momentConfig = momentService.ruleTypeToConfig(key);
    return _.isUndefined(momentConfig) ? '' : momentService.systemNow(momentConfig);
  }
  function invalidateCache() {
    getSearchContext.cache.clear();
  }
  function isAtCorrectLevel(context, level, categoryName) {
    var category = context.categories[level];
    if (!category) {
      return false;
    }
    return !!category[categoryName];
  }
  function isCategoryAvailableForFeatures(context, categoryName) {
    return !(context.showIfFeatureState[categoryName] && featureToggleService.isDisabled(context.showIfFeatureState[categoryName]));
  }
  return {
    getLevels: getLevels,
    getCategories: getCategories,
    getCriteria: getCriteria,
    getCriteriaNameForData: getCriteriaNameForData,
    getDefaultFieldValue: getDefaultFieldValue,
    getOperators: getOperators,
    getValueType: getValueType,
    getField: getField,
    getSelectionLimit: getSelectionLimit,
    getMetaCategory: getMetaCategory,
    getShortCriterionName: getShortCriterionName,
    getOperandLimits: getOperandLimits,
    invalidateCache: invalidateCache
  };
};
CriteriaRuleService.$inject = ['$log', 'ConfigurationService', 'MomentService', 'FeatureToggleService', 'RuleVisibilityService'];
module.exports = CriteriaRuleService;